import { useElements, useStripe } from "@stripe/react-stripe-js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Info, Pencil, TriangleAlert } from "lucide-react";
import { useState } from "react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { Skeleton } from "@/components/ui/skeleton";

import { stripeUrl } from "common/const";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import { usePermissions } from "components/permissions/usePermissions";
import { EditPaymentMethodDialogue } from "components/settings/shared/billing-details/editPaymentDialogue";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { addPaymentMethod } from "data/mutations-common";
import { fetchPaymentMethod } from "data/queries-common";
import { toTitleCase } from "utils/commons";
import { resolveIdentityId } from "utils/identity";

export const PaymentMethodCard = () => {
  const [identity] = useIdentity();
  const identityId = resolveIdentityId(identity);
  const queryClient = useQueryClient();
  const stripe = useStripe();
  const stripeElements = useElements();
  const [editingCC, setEditingCC] = useState(false);

  const paymentMethodQuery = useQuery({
    queryKey: [QueryKeys.paymentMethod, identityId],
    queryFn: () => fetchPaymentMethod(),
  });

  const { isPermitted, _permissionDeniedReason } = usePermissions();
  const isExternallyManaged =
    _permissionDeniedReason([PermissionsKeys.paymentMethodUpdate]) ===
    PermissionsReasons.EXTERNALLY_MANAGED;
  const canViewPaymentMethod =
    isPermitted([PermissionsKeys.paymentMethodRead]) && !isExternallyManaged;
  const canEdit = isPermitted([PermissionsKeys.paymentMethodUpdate]);

  const paymentMethodMutation = useMutation({
    mutationFn: () => addPaymentMethod(stripe, stripeElements, setEditingCC),
    onSuccess: (newPaymentMethod) => {
      queryClient.setQueryData(
        [QueryKeys.paymentMethod, identityId],
        newPaymentMethod,
      );
    },
  });

  const handleOnClose = () => {
    paymentMethodMutation.reset();
    setEditingCC(false);
  };

  const paymentMethodValue = paymentMethodQuery.data
    ? `${toTitleCase(paymentMethodQuery.data.method)} .... ${
        paymentMethodQuery.data.last4
      } - ${paymentMethodQuery.data.expireMonth}/${
        paymentMethodQuery.data.expireYear
      }`
    : "No Payment Method";

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment Method</CardTitle>
        <CardDescription>
          The card used for subscription and overage charges.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {paymentMethodQuery.isError && (
          <Alert variant="destructive">
            <TriangleAlert />
            <AlertDescription>
              An error occurred while fetching the payment method.
            </AlertDescription>
          </Alert>
        )}

        {paymentMethodQuery.isPending && (
          <Skeleton className="h-20 w-full" />
        )}

        {paymentMethodQuery.isSuccess && (
          <>
            {isExternallyManaged && (
              <Alert variant="info">
                <Info />
                <AlertDescription>
                  Your payment is managed outside of self-service, please
                  contact support for any changes.
                </AlertDescription>
              </Alert>
            )}

            {canViewPaymentMethod && (
              <>
                <Field>
                  <FieldLabel htmlFor="paymentMethod">
                    Payment Method
                  </FieldLabel>
                  <InputGroup>
                    <InputGroupInput
                      id="paymentMethod"
                      readOnly
                      value={paymentMethodValue}
                    />
                    {canEdit && (
                      <InputGroupAddon align="inline-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditingCC(true)}
                        >
                          <Pencil />
                        </Button>
                      </InputGroupAddon>
                    )}
                  </InputGroup>
                </Field>

                {paymentMethodQuery.data?.expired && (
                  <Alert variant="destructive">
                    <TriangleAlert />
                    <AlertDescription>
                      Your credit card has expired
                    </AlertDescription>
                  </Alert>
                )}

                <EditPaymentMethodDialogue
                  open={editingCC}
                  handleOnClose={handleOnClose}
                  stripeUrl={stripeUrl}
                  paymentMethodMutation={paymentMethodMutation}
                />
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
